import { BaseModule } from "@/models/BaseModule"
import { HttpError } from "@/models/Error"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { Action, Module, Mutation } from "vuex-class-modules"
import { Modules } from "../modules"
import { store } from "../store"
import { accountModule } from "./account.module"
import { AIConfigCurrent, AIProfile, AIPromptDetail, AIPromptSimplify, AIConfig, AlertContentItem, AlertType, ChatGPTResponse, Filter, ItemAcceptanceCriteriaRequest, ServerResponse, SprintAnalyticsInsightRequest, AiConversations, AiConversationMessage, AiConversationMessageResponse, ConversationParams, SendAiMessageRequest, AiPageContext, AiChatMode } from "@/models"
import { projectModule } from "./project.module"
import { AlertActions, alertModule } from "./alert.module"
import { sessionModule } from "./session.module"

export enum ChatGPTActions {
  CHAT_GPT_ASK = "CHAT_GPT_ASK",
  CHAT_GPT_RESET = "CHAT_GPT_RESET",
  SPRINT_ANALYTICS_INSIGHTS = "SPRINT_ANALYTICS_INSIGHTS",
  GET_AI_PROFILE = "GET_AI_PROFILE",
  AI_CONFIG_CREATE = "AI_CONFIG_CREATE",
  AI_CONFIG_UPDATE = "AI_CONFIG_UPDATE",
  AI_CONFIG_TOKEN_CHECK = "AI_CONFIG_TOKEN_CHECK",
  POST_AI_CONFIG = "POST_AI_CONFIG",
  PUT_AI_CONFIG = "PUT_AI_CONFIG",
  GET_AI_PROMPTS = "GET_AI_PROMPTS",
  GET_AI_PROMPT = "GET_AI_PROMPT",
  UPDATE_AI_PROMPT = "UPDATE_AI_PROMPT",
  UPDATE_AI_PROMPT_ENABLED = "UPDATE_AI_PROMPT_ENABLED",
  AI_PROMPT_RUN = "AI_PROMPT_RUN",
  GET_AI_CONFIG = "GET_AI_CONFIG",
  GET_AI_CONVERSATION = "GET_AI_CONVERSATION",
  POST_AI_CONVERSATION_MESSAGE = "POST_AI_CONVERSATION_MESSAGE",
  CLEAR_AI_CONVERSATION_CONTEXT = "CLEAR_AI_CONVERSATION_CONTEXT",
  DELETE_AI_CONVERSATION_MESSAGES = "DELETE_AI_CONVERSATION_MESSAGES",
  REMOVE_CURRENT_AI_CONVERSATION_MESSAGE = "REMOVE_CURRENT_AI_CONVERSATION_MESSAGE",
  SET_CONVERSATION_PARAMS = "SET_CONVERSATION_PARAMS",
  GET_CONVERSATION_PARAMS = "GET_CONVERSATION_PARAMS",
  GET_AI_CONTEXT_TEAM_ID = "GET_AI_CONTEXT_TEAM_ID",
  SET_AI_CONTEXT_TEAM_IDS = "SET_AI_CONTEXT_TEAM_IDS",
  GET_AI_CONTEXT_TEAM_IDS = "GET_AI_CONTEXT_TEAM_IDS",
  SET_AI_CHAT_MODE = "SET_AI_CHAT_MODE",
  GET_AI_CHAT_MODE = "GET_AI_CHAT_MODE",
  SET_AI_CHAT_IN_PIP = "SET_AI_CHAT_IN_PIP",
  GET_AI_CHAT_IN_PIP = "GET_AI_CHAT_IN_PIP",
  ITEM_AI_ACCEPTANCE_CRITERIA = "ITEM_AI_ACCEPTANCE_CRITERIA",
  ITEM_AI_ACCEPTANCE_CRITERIA_REGENERATE = "ITEM_AI_ACCEPTANCE_CRITERIA_REGENERATE"
}

@Module
class ChatGPTModule extends BaseModule {
  aiConfig: AIConfig = new AIConfig()
  currentAiConversation: AiConversations = new AiConversations()
  conversationParams: ConversationParams = new ConversationParams()
  aiContextTeamIds: string[] = []
  aiChatMode: AiChatMode = "compact"
  isAiChatInPip = false

  get accountId() {
    return accountModule.accountId
  }

  get projectId() {
    return projectModule.currentProjectId
  }

  get [`get/${ChatGPTActions.GET_AI_PROFILE}`]() {
    return new AIProfile(this.aiConfig.current)
  }

  get [`get/${ChatGPTActions.GET_AI_CONFIG}`]() {
    return this.aiConfig
  }

  get [`get/${ChatGPTActions.GET_AI_CONVERSATION}`]() {
    return this.currentAiConversation
  }

  get [`get/${ChatGPTActions.GET_CONVERSATION_PARAMS}`]() {
    return this.conversationParams
  }

  get [`get/${ChatGPTActions.GET_AI_CHAT_MODE}`]() {
    return this.aiChatMode
  }

  get [`get/${ChatGPTActions.GET_AI_CHAT_IN_PIP}`]() {
    return this.isAiChatInPip
  }

  /** @deprecated Remove after all frontend clients use GET_AI_CONTEXT_TEAM_IDS. */
  get [`get/${ChatGPTActions.GET_AI_CONTEXT_TEAM_ID}`]() {
    return this[`get/${ChatGPTActions.GET_AI_CONTEXT_TEAM_IDS}`][0] ?? ""
  }

  /** Returns the active page-filter teams, falling back to the current session team. */
  get [`get/${ChatGPTActions.GET_AI_CONTEXT_TEAM_IDS}`]() {
    return this.aiContextTeamIds.length ? this.aiContextTeamIds : [sessionModule.teamId].filter(Boolean)
  }

  @Mutation
  setAiConfig(aiConfig: AIConfig) {
    this.aiConfig = aiConfig
  }

  @Mutation
  setAiConfigCurrent(aiConfigCurrent: AIConfigCurrent) {
    this.aiConfig.current = aiConfigCurrent
  }

  @Mutation
  updateAiProfile(aiProfile: AIProfile) {
    this.aiConfig.current = new AIConfigCurrent({ ...this.aiConfig.current, ...aiProfile })
  }

  @Mutation
  setCurrentAiConversation(aiConversation: AiConversations) {
    this.currentAiConversation = aiConversation
  }

  @Mutation
  setCurrentAiConversationMessage(aiConversationMessage: AiConversationMessage) {
    const findMessage = this.currentAiConversation.messages.find(message => message.id === aiConversationMessage.id)
    if (!findMessage) {
      this.currentAiConversation.messages.push(aiConversationMessage)
    } else {
      this.currentAiConversation.messages = this.currentAiConversation.messages.map(message => message.id === aiConversationMessage.id ? aiConversationMessage : message)
    }
  }

  @Mutation
  replacePendingCurrentAiConversationMessage({
    pendingMessageId,
    aiConversationMessage
  } : {
    pendingMessageId: string
    aiConversationMessage: AiConversationMessageResponse
  }) {
    const pendingMessageIndex = this.currentAiConversation.messages.findIndex(
      message => message.id === pendingMessageId
    )
    const messages = [
      aiConversationMessage.userMessage,
      aiConversationMessage.assistantMessage
    ]

    if (pendingMessageIndex === -1) {
      this.currentAiConversation.messages.push(...messages)
    } else {
      this.currentAiConversation.messages.splice(pendingMessageIndex, 1, ...messages)
    }

    if (aiConversationMessage.conversationModifiedDate) {
      this.currentAiConversation.modifiedDate = aiConversationMessage.conversationModifiedDate
    }
  }

  @Mutation
  removeCurrentAiConversationMessage(messageId: string) {
    this.currentAiConversation.messages = this.currentAiConversation.messages.filter(
      message => message.id !== messageId
    )
  }

  @Mutation
  setAiConversationMessages(messages: AiConversationMessage[]) {
    this.currentAiConversation.messages = messages
  }

  @Mutation
  setConversationParams(params: ConversationParams) {
    this.conversationParams = params
  }

  /** Stores the team ids already resolved by the page filter. */
  @Mutation
  setAiContextTeams(teamIds: string[]) {
    this.aiContextTeamIds = teamIds
  }

  @Mutation
  setAiChatMode(mode: AiChatMode) {
    this.aiChatMode = mode
  }

  @Mutation
  setAiChatInPip(isInPip: boolean) {
    this.isAiChatInPip = isInPip
  }

  @Action
  async [ChatGPTActions.CHAT_GPT_ASK](prompt: string) {
    const data = await apiService.post<ChatGPTResponse>(Endpoint.CHAT_GPT_ASK(this.accountId), prompt)
    return this.handleResponse<ChatGPTResponse>(data, data => data, true)
  }

  @Action
  async [ChatGPTActions.SPRINT_ANALYTICS_INSIGHTS]({ sprintId, bodyParams }: { sprintId: string; bodyParams: SprintAnalyticsInsightRequest }) {
    const data = await apiService.post<ChatGPTResponse>(Endpoint.SPRINT_ANALYTICS_INSIGHTS(this.projectId, sprintId), bodyParams)
    return this.handleResponse<ChatGPTResponse>(data, data => data, true)
  }

  @Action
  async [ChatGPTActions.CHAT_GPT_RESET]() {
    const data = await apiService.post<void>(Endpoint.CHAT_GPT_RESET(this.accountId))
    return this.handleResponse<void>(data, undefined, true)
  }

  @Action
  async [ChatGPTActions.GET_AI_CONFIG]() {
    const data = await apiService.get<AIConfig>(Endpoint.GET_AI_CONFIG(this.accountId))
    return this.handleResponse<AIConfig>(data, data => {
      const aiConfig = new AIConfig(data)
      this.setAiConfig(aiConfig)
      return aiConfig
    })
  }

  @Action
  async [ChatGPTActions.AI_CONFIG_CREATE]({ aiConfig }: { aiConfig: AIConfigCurrent }) {
    aiConfig.accountId = this.accountId
    const data = await apiService.post<AIConfigCurrent>(Endpoint.AI_CONFIG_CREATE(this.accountId), aiConfig.getJsonObj())
    return this.handleResponse<AIConfigCurrent>(data, data => {
      const content: AlertContentItem[] = [
        {
          text: "AI config ",
          type: "bold",
        },
        {
          text: "was created",
          type: "regular"
        }
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content
      })
      return new AIConfigCurrent(data)
    })
  }
  
  @Action
  async [ChatGPTActions.AI_CONFIG_UPDATE]({ aiConfig }: { aiConfig: AIConfigCurrent }) {
    aiConfig.accountId = this.accountId
    const data = await apiService.put<AIConfigCurrent>(Endpoint.AI_CONFIG_UPDATE(this.accountId, aiConfig.id), aiConfig.getJsonObj())
    return this.handleResponse<AIConfigCurrent>(data, data => {
      const content: AlertContentItem[] = [
        {
          text: "AI config ",
          type: "bold",
        },
        {
          text: "was updated",
          type: "regular"
        }
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content
      })
      return new AIConfigCurrent(data)
    })
  }

  @Action
  async [ChatGPTActions.AI_CONFIG_TOKEN_CHECK]({ provider, apiKey } : { provider: string; apiKey: string }) {
    const data = await apiService.post<boolean>(Endpoint.AI_CONFIG_TOKEN_CHECK(this.accountId), { provider, apiKey })
    return this.handleResponse<boolean>(data, data => {
      return data.toString()
    })
  }

  @Action
  async [ChatGPTActions.GET_AI_PROFILE]() {
    const data = await apiService.get<AIProfile>(Endpoint.AI_CONFIG_PROFILE(this.accountId))
    return this.handleResponse<AIProfile>(data, data => {
      const aiProfile = new AIProfile(data)
      this.updateAiProfile(aiProfile)
      return aiProfile
    })
  }

  @Action
  async [ChatGPTActions.PUT_AI_CONFIG]({ aiConfig }: { aiConfig: AIConfigCurrent }) {
    aiConfig.accountId = this.accountId
    const data = await apiService.put<AIConfigCurrent>(Endpoint.AI_CONFIG_BY_ID(this.accountId, aiConfig.id), aiConfig.getJsonObj())
    return this.handleResponse<AIConfigCurrent>(data, data => {
      const aiConfig = new AIConfigCurrent(data)
      this.setAiConfigCurrent(aiConfig)
      const content: AlertContentItem[] = [
        {
          text: "AI config ",
          type: "bold",
        },
        {
          text: "changes saved",
          type: "regular"
        },
        {
          text: " successfully",
          type: "bold"
        }
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content
      })
      return aiConfig
    })
  }

  @Action
  async [ChatGPTActions.POST_AI_CONFIG]({ aiConfig }: { aiConfig: AIConfigCurrent }) {
    aiConfig.accountId = this.accountId
    const data = await apiService.post<AIConfigCurrent>(Endpoint.AI_CONFIG(this.accountId), aiConfig.getJsonObj())
    return this.handleResponse<AIConfigCurrent>(data, data => {
      const aiConfig = new AIConfigCurrent(data)
      this.setAiConfigCurrent(aiConfig)
      return aiConfig
    })
  }

  @Action
  async [ChatGPTActions.GET_AI_PROMPTS]({ filter } : { filter: Filter }) {
    const data = await apiService.get<ServerResponse<AIPromptSimplify>>(Endpoint.GET_AI_PROMPTS(this.projectId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<AIPromptSimplify>>(data, data => {
      return { ...data, content: data.content.map(item => new AIPromptSimplify(item)) }
    })
  }

  @Action
  async [ChatGPTActions.GET_AI_PROMPT]({ promptId } : { promptId: string }) {
    const data = await apiService.get<AIPromptDetail>(Endpoint.AI_PROMPT(this.projectId, promptId))
    return this.handleResponse<AIPromptDetail>(data, data => {
      return new AIPromptDetail(data)
    })
  }

  @Action
  async [ChatGPTActions.UPDATE_AI_PROMPT]({ promptDetail } : { promptDetail: AIPromptDetail }) {
    const data = await apiService.put<AIPromptDetail>(Endpoint.AI_PROMPT(this.projectId, promptDetail.id), promptDetail.getJsonObj())
    return this.handleResponse<AIPromptDetail>(data, data => {
      const content: AlertContentItem[] = [
        {
          text: "AI prompt ",
          type: "bold",
        },
        {
          text: "was updated",
          type: "regular"
        }
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content
      })
      return new AIPromptDetail(data)
    })
  }

  /** Updates whether a project AI prompt is available to application workflows. */
  @Action
  async [ChatGPTActions.UPDATE_AI_PROMPT_ENABLED]({ promptId, enabled } : { promptId: string; enabled: boolean }) {
    const data = await apiService.patch<boolean>(Endpoint.AI_PROMPT_ENABLED(this.projectId, promptId), null, {
      params: { enabled }
    })

    return this.handleResponse<boolean>(data, () => {
      const content: AlertContentItem[] = [
        {
          text: "AI prompt ",
          type: "bold",
        },
        {
          text: enabled ? "was enabled" : "was disabled",
          type: "regular"
        }
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content
      })
    })
  }

  @Action
  async [ChatGPTActions.AI_PROMPT_RUN](
    {
      promptId,
      prompt,
      systemPrompt,
      exampleInputJson
    } : {
      promptId: string
      prompt: string
      systemPrompt: string
      exampleInputJson: string
    }
  ) {
    const data = await apiService.post<any>(
      Endpoint.AI_PROMPT_RUN(this.projectId, promptId, this.accountId),
      { prompt, systemPrompt, exampleInputJson }
    )
    return this.handleResponse<any>(data, data => {
      return data
    }, true, error => ({ error }))
  }

  /** Omits page identifiers for the account-wide chat while preserving legacy context payloads. */
  @Action
  async [ChatGPTActions.GET_AI_CONVERSATION]({ conversationParams } : { conversationParams: ConversationParams }) {
    const request = conversationParams.contextType === "GENERAL"
      ? { contextType: "GENERAL" }
      : conversationParams
    const data = await apiService.post<AiConversations>(
      Endpoint.AI_CONVERSATION_GET_OR_CREATE(this.accountId),
      request
    )
    return this.handleResponse<AiConversations>(data, data => {
      const aiConversation = new AiConversations(data)
      this.setCurrentAiConversation(aiConversation)
      return aiConversation
    })
  }

  @Action
  async [ChatGPTActions.CLEAR_AI_CONVERSATION_CONTEXT]({
    conversationId
  } : {
    conversationId: string
  }) {
    const data = await apiService.post<void>(
      Endpoint.AI_CONVERSATION_CLEAR_CONTEXT(this.accountId, conversationId),
      {}
    )
    return this.handleResponse<void>(data, undefined, true)
  }

  @Action
  async [ChatGPTActions.DELETE_AI_CONVERSATION_MESSAGES]({
    conversationId
  } : {
    conversationId: string
  }) {
    const data = await apiService.delete<AiConversations>(
      Endpoint.AI_CONVERSATION_MESSAGES_DELETE(this.accountId, conversationId)
    )
    return this.handleResponse<AiConversations>(data, data => {
      const aiConversation = new AiConversations(data)
      this.setCurrentAiConversation(aiConversation)
      return aiConversation
    })
  }

  /** Sends page context and conversation upload IDs with a stable ID for safe retries; a reply for a conversation that is no longer current is not written into the current one. */
  @Action
  async [ChatGPTActions.POST_AI_CONVERSATION_MESSAGE]({
    conversationId,
    message,
    context,
    attachmentIds = [],
    clientMessageId
  } : {
    conversationId: string
    message: string
    context: AiPageContext
    attachmentIds?: string[]
    clientMessageId?: string
  }) {
    const userMessage = new AiConversationMessage({
      content: message,
      role: "USER",
      id: clientMessageId || Date.now().toString(),
      createdDate: new Date().toISOString(),
      ordinal: this.currentAiConversation.messages.length
    })
    this.setCurrentAiConversationMessage(userMessage)
    const data = await apiService.post<AiConversationMessageResponse>(
      Endpoint.AI_CONVERSATION_MESSAGES(this.accountId, conversationId),
      new SendAiMessageRequest({
        message,
        context,
        attachmentIds,
        clientMessageId
      })
    )
    return this.handleResponse<AiConversationMessageResponse>(data, data => {
      const aiConversationMessage = new AiConversationMessageResponse(data)
      if (this.currentAiConversation.id === conversationId) {
        this.replacePendingCurrentAiConversationMessage({
          pendingMessageId: userMessage.id,
          aiConversationMessage
        })
      }
      return aiConversationMessage
    }, true, (error: HttpError) => {
      if (this.currentAiConversation.id !== conversationId) return
      // The chat marks the message itself; a global alert would appear behind the widget.
      this.setCurrentAiConversationMessage({
        ...userMessage,
        notSent: true,
        errorMessage: error.response?.data?.message || error.message || ""
      })
    })
  }

  @Action
  async [ChatGPTActions.REMOVE_CURRENT_AI_CONVERSATION_MESSAGE](messageId: string) {
    this.removeCurrentAiConversationMessage(messageId)
  }
  
  @Action
  async [ChatGPTActions.SET_CONVERSATION_PARAMS](conversationParams: ConversationParams) {
    this.setConversationParams(conversationParams)
  }

  /** Synchronizes the teams represented by the current page filter. */
  @Action
  [ChatGPTActions.SET_AI_CONTEXT_TEAM_IDS](teamIds: string[]) {
    this.setAiContextTeams(teamIds)
  }

  /** Remembers the chat mode for the session so the chat reopens in it. */
  @Action
  [ChatGPTActions.SET_AI_CHAT_MODE](mode: AiChatMode) {
    this.setAiChatMode(mode)
  }

  /** Kept apart from the size mode so returning from PiP restores the size it was opened from. */
  @Action
  [ChatGPTActions.SET_AI_CHAT_IN_PIP](isInPip: boolean) {
    this.setAiChatInPip(isInPip)
  }

  @Action
  async [ChatGPTActions.ITEM_AI_ACCEPTANCE_CRITERIA](bodyParams: ItemAcceptanceCriteriaRequest) {
    const data = await apiService.post<string>(Endpoint.ITEM_AI_ACCEPTANCE_CRITERIA(this.accountId, this.projectId), bodyParams)
    return this.handleResponse<string>(data, data => data, true, undefined, true)
  }

  @Action
  async [ChatGPTActions.ITEM_AI_ACCEPTANCE_CRITERIA_REGENERATE](bodyParams: ItemAcceptanceCriteriaRequest) {
    const data = await apiService.post<string>(Endpoint.ITEM_AI_ACCEPTANCE_CRITERIA_REGENERATE(this.accountId, this.projectId), bodyParams)
    return this.handleResponse<string>(data, data => data, true, undefined, true)
  }
}

export const chatGPTModule = new ChatGPTModule({
  store,
  name: Modules.CHAT_GPT,
})
